import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "../supabaseClient";
import { T, ALL_LABELS } from "../lib/i18n";
import { scoreColor, openCodeSheet, compareGroupNames } from "../lib/helpers";
import { serverNow, startPolling, useServerTrouble } from "../lib/net";
import { useVersionCheck } from "../lib/version";
import { useTabSleep } from "../lib/sleep";
import { dbLoadSession, dbToggleRationale, dbToggleLock, dbSetTimer, dbAdjustGroupScore, dbExtendAllGroupTimers, dbLoadAllResultsForDash, dbRenameGroup, dbDeleteGroup, dbLoadAllAppeals, dbUpdateAppeal, dbLoadAllBQ, dbSaveDelegation, dbLoadAllDelegations, dbCanViewSession } from "../lib/db";
import { exportXLSX, exportPDF } from "../lib/export";
import { Pencil, Trash2 } from "lucide-react";
import { Footer, QuestionImages, QuestionText, RichText, ConfirmDialog, ServerTroubleBanner } from "../components/common";
import { QuestionPreview } from "./QuestionPreview";
import { sessionStatus, statusMeta } from "../lib/status";
import { appealContext, scoreAfterDeduction, decisionPoints, APPROVE_SCORES, REJECT_DEDUCTIONS } from "../lib/appeals";

// ─────────────────────────────────────────
//  DASHBOARD
// ─────────────────────────────────────────
const DASH_REFRESH_GAP = 3000; // ms mellan realtidsutlösta hämtningar

function DashboardScreen({ dashId, onBack, onHome, lang, setLang, theme, setTheme, G, css, user, authLoading = false }) {
  const [session, setSession] = useState(null);
  const [results, setResults] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [bqs, setBqs] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [tab, setTab] = useState("scores");
  const [appealStatuses, setAppealStatuses] = useState({});
  // Pågående beslut på ett appeal-kort: { key, kind: "approve" | "reject", value }
  const [deciding, setDeciding] = useState(null);
  const [delegatingBQ, setDelegatingBQ] = useState(null);
  const [delegateTarget, setDelegateTarget] = useState("");
  const [delegatedKeys, setDelegatedKeys] = useState({});
  const [showRationale, setShowRationale] = useState(false);
  const [sessionLocked, setSessionLocked] = useState(false);
  const [timerDuration, setTimerDuration] = useState(10); // minutes
  const [timerEnd, setTimerEnd] = useState(null); // ISO string
  const [timerNow, setTimerNow] = useState(serverNow());
  const [expandedAppealQ, setExpandedAppealQ] = useState(null); // appeal._key or bq._key
  const [expandedQRow, setExpandedQRow] = useState(null); // qi index in question analysis
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showGroupCodes, setShowGroupCodes] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [groupMenu, setGroupMenu] = useState(null); // groupName or null
  const [editingGroupName, setEditingGroupName] = useState(null); // { old, value }
  const [deleteConfirmGroup, setDeleteConfirmGroup] = useState(null); // groupName
  const [unseenAppeals, setUnseenAppeals] = useState(0);
  const [unseenBQs, setUnseenBQs] = useState(0);
  const [lastSeenAppeals, setLastSeenAppeals] = useState(Date.now());
  const [lastSeenBQs, setLastSeenBQs] = useState(Date.now());
  const t = T[lang];

  // Att avsluta stoppar allt studentarbete — inte bara skrapning, utan även
  // appeals och burning questions. Bekräfta så konsekvensen framgår; att öppna
  // igen är ofarligt och sker direkt.
  const [confirmEnd, setConfirmEnd] = useState(false);
  function requestToggleLock() {
    if (sessionLocked) applyToggleLock();
    else setConfirmEnd(true);
  }

  async function applyToggleLock() {
    setConfirmEnd(false);
    await toggleLock();
  }

  async function toggleLock() {
    const newLocked = !sessionLocked;
    setSessionLocked(newLocked);
    try {
      await dbToggleLock(session.sessionId, newLocked);
    } catch (e) {
      // Revert the optimistic flip — a lock that LOOKS applied but isn't
      // means students keep answering while the teacher believes it's closed
      setSessionLocked(!newLocked);
      console.error("toggleLock failed:", e);
    }
  }

  async function startTimer(durationOverride) {
    try {
      const mins = (typeof durationOverride === 'number' && durationOverride > 0) ? durationOverride : timerDuration;
      const end = new Date(serverNow() + mins * 60 * 1000).toISOString();
      setTimerEnd(end);
      await dbSetTimer(session.sessionId, end);
    } catch (e) {
      console.error("startTimer failed:", e);
    }
  }

  // Ett namnbyte får inte slå ihop två grupper (appeals och BQ skulle hamna
  // hos fel grupp): ett namn som redan finns godtas inte.
  function renameAllowed(ed) {
    const v = ed?.value?.trim();
    return !!v && v !== ed.old && !results.some(r => r.groupName === v);
  }

  // Misslyckas förlängningen får läraren veta det och klockan går tillbaka —
  // annars visar dashboarden längre tid än servern, som avslutar grupperna.
  const [timerErr, setTimerErr] = useState(false);
  async function addTime(extraMins) {
    const before = timerEnd;
    const currentEnd = timerEnd ? new Date(timerEnd).getTime() : serverNow();
    const base = Math.max(currentEnd, serverNow()); // if expired, extend from now
    const newEnd = new Date(base + extraMins * 60 * 1000).toISOString();
    setTimerEnd(newEnd);
    setTimerErr(false);
    try {
      await dbSetTimer(session.sessionId, newEnd);
      // Also extend all per-group timers by the same amount
      await dbExtendAllGroupTimers(session.sessionId, extraMins);
    } catch (e) {
      console.error("addTime failed:", e);
      setTimerEnd(before);
      setTimerErr(true);
    }
  }

  async function stopTimer() {
    setTimerEnd(null);
    await dbSetTimer(session.sessionId, null);
  }


  const timerSecsLeft = timerEnd ? Math.max(0, Math.floor((new Date(timerEnd).getTime() - timerNow) / 1000)) : null;
  const timerMins = timerSecsLeft != null ? Math.floor(timerSecsLeft / 60) : null;
  const timerSecs = timerSecsLeft != null ? timerSecsLeft % 60 : null;
  const timerExpired = timerSecsLeft === 0;

  // Bara den senast startade hämtningen får skriva: ett långsamt svar som
  // kommer efter ett nyare ska inte visa gammal data.
  const refreshSeq = useRef(0);
  const refresh = useCallback(async () => {
    if (!session) return;
    const seq = ++refreshSeq.current;
    const [r, a, b, d] = await Promise.all([dbLoadAllResultsForDash(dashId), dbLoadAllAppeals(session.sessionId), dbLoadAllBQ(session.sessionId), dbLoadAllDelegations(session.sessionId)]);
    if (seq !== refreshSeq.current) return;
    setResults(r); setAppeals(a); setBqs(b); setDelegations(d); setLastRefresh(Date.now());
  }, [session, dashId]);

  // Laddas om bara när en annan person loggar in — inte när inloggningen
  // förnyas (ungefär varje timme och när fliken får fokus igen), vilket ger
  // ett nytt user-objekt men samma person.
  const userId = user?.id || null;
  useEffect(() => {
    if (authLoading) return; // Wait for auth to resolve
    setLoading(true); setErr(null); setSession(null);
    // Ett tillfälligt fel ska inte lämna läraren på en felsida: ett nytt försök
    const loadOnce = () => dbLoadSession(dashId);
    loadOnce().then(s => s || new Promise(r => setTimeout(r, 1500)).then(loadOnce)).then(async s => {
      if (!s || !s.dashId) { setErr(t.dashNotFound); setLoading(false); return; }
      // Authorization is decided server-side (can_view_session): the owner, a
      // colleague teaching the same course, or anyone holding the dash code of a
      // legacy session without a teacher.
      if (s.teacherId) {
        const allowed = userId ? await dbCanViewSession(s.sessionId) : false;
        if (!allowed) {
          setErr(lang === "sv"
            ? (userId ? "Du har inte behörighet att se denna dashboard." : "Logga in för att se denna dashboard.")
            : (userId ? "You do not have permission to view this dashboard." : "Sign in to view this dashboard."));
          setLoading(false);
          return;
        }
      }
      setSession(s); setShowRationale(s.showRationale || false); setSessionLocked(s.sessionLocked || false);
      if (s.config?.timer_end) setTimerEnd(s.config.timer_end);
      if (s.config?.session_duration) setTimerDuration(s.config.session_duration);
      Promise.all([dbLoadAllResultsForDash(dashId), dbLoadAllAppeals(s.sessionId), dbLoadAllBQ(s.sessionId), dbLoadAllDelegations(s.sessionId)]).then(([r, a, b, d]) => { setResults(r); setAppeals(a); setBqs(b); setDelegations(d); setLoading(false); });
    });
  }, [dashId, userId, authLoading]);

  // Realtime: refresh when results/appeals/BQs/delegations change.
  // A slow poll remains as fallback (tables outside the realtime publication,
  // dropped websocket etc.) — previously this polled 4 queries every 8s.
  // Har fliken legat dold i en halvtimme somnar den (se useTabSleep): även
  // realtiden stängs, och allt hämtas direkt när den syns igen. En synlig
  // dashboard somnar aldrig — den kan stå på en projektor.
  const { asleep, hasSlept } = useTabSleep();
  useEffect(() => {
    if (!session || asleep) return;
    // Högst en hämtning var 3:e sekund, aldrig två samtidigt, och ingen alls
    // medan fliken är dold (den hämtar när den syns igen). Förut gjordes fyra
    // databasfrågor per skrap i hela salen, per öppen dashboard.
    let t = null, running = false, again = false, last = 0, stopped = false;
    const run = async () => {
      if (running) { again = true; return; }
      running = true; last = Date.now();
      try { await refresh(); } catch { /* nästa händelse eller poll försöker igen */ }
      running = false;
      if (again) { again = false; refreshSoon(); }
    };
    const refreshSoon = () => {
      if (stopped || t || document.visibilityState === "hidden") return;
      const wait = Math.max(250, DASH_REFRESH_GAP - (Date.now() - last));
      t = setTimeout(() => { t = null; run(); }, wait);
    };
    const ch = supabase.channel(`dash-${session.sessionId}`);
    // group_progress is included so the dashboard also updates live for legacy
    // sessions opened with just the dash code (no login → no group_results events)
    ["group_results", "group_progress", "appeals", "burning_questions", "delegations"].forEach(table => {
      ch.on("postgres_changes", { event: "*", schema: "public", table, filter: `session_id=eq.${session.sessionId}` }, refreshSoon);
    });
    ch.subscribe();
    // Pausar när fliken är dold och hämtar direkt när den syns igen.
    const stopPoll = startPolling(async () => { await refresh(); }, { interval: 30000, report: false, immediate: hasSlept });
    return () => { stopped = true; clearTimeout(t); stopPoll(); supabase.removeChannel(ch); };
  }, [session, refresh, asleep, hasSlept]);
  useEffect(() => { const i = setInterval(() => setTimerNow(serverNow()), 500); return () => clearInterval(i); }, []);

  // Svarar servern? Ett lätt anrop var 30:e sekund; efter två fel i rad visas
  // en ruta, så att läraren vet att felet inte ligger hos dem.
  const serverTrouble = useServerTrouble();
  useVersionCheck(undefined, asleep);
  useEffect(() => startPolling(async () => {
    const { error } = await supabase.rpc("now");
    return !error;
  }, { interval: 30000 }), []);

  // Track unseen appeals/BQs
  useEffect(() => {
    const newAppeals = appeals.filter(a => a.created_at && new Date(a.created_at).getTime() > lastSeenAppeals).length;
    setUnseenAppeals(newAppeals);
  }, [appeals, lastSeenAppeals]);
  useEffect(() => {
    const newBQs = bqs.filter(b => b.created_at && new Date(b.created_at).getTime() > lastSeenBQs).length;
    setUnseenBQs(newBQs);
  }, [bqs, lastSeenBQs]);

  // Update document title with unseen count
  useEffect(() => {
    const total = unseenAppeals + unseenBQs;
    document.title = total > 0 ? `(${total}) Dashboard · tRAT` : 'Dashboard · tRAT';
    return () => { document.title = 'tRAT'; };
  }, [unseenAppeals, unseenBQs]);

  function activityStatus(updatedAt) {
    if (!updatedAt) return 'idle';
    const mins = (Date.now() - new Date(updatedAt).getTime()) / 60000;
    if (mins < 2) return 'active';
    if (mins < 10) return 'recent';
    return 'idle';
  }
  const activityDotColor = { active: G.correct, recent: G.gold, idle: G.border };

  async function toggleRationale() {
    const v = !showRationale; setShowRationale(v);
    await dbToggleRationale(session.sessionId, v);
  }

  // Godkänn sätter frågans nya poäng; avslå kan dra av poäng från frågan.
  // appeals.adjusted_score får frågans nya poäng vid godkännande och avdraget
  // vid avslag (se decisionPoints).
  async function handleAppealDecision(appeal, kind, value) {
    const status = kind === "approve" ? "approved" : "rejected";
    let newScore = null;
    if (kind === "approve") newScore = value;
    else if (value < 0) {
      const current = results.find(r => r.groupName === appeal.groupName)?.scores?.[appeal.questionIndex];
      newScore = scoreAfterDeduction(current, value);
    }
    const stored = kind === "approve" ? value : (value < 0 ? value : null);
    await dbUpdateAppeal(appeal._key, status, stored);
    setAppealStatuses(p => ({ ...p, [appeal._key]: { status, adjustedScore: stored } }));
    if (newScore !== null) {
      const updated = await dbAdjustGroupScore(appeal.sessionId || session.sessionId, appeal.groupName, appeal.questionIndex, newScore);
      if (updated) {
        setResults(p => p.map(r => r.groupName === appeal.groupName ? { ...r, scores: updated.scores, total: updated.total } : r));
      }
    }
    setDeciding(null);
  }

  async function handleDelegate(bq) {
    if (!delegateTarget) return;
    await dbSaveDelegation(session.sessionId, bq._key, delegateTarget, bq.groupName, bq.text, bq.iFATQuestion);
    setDelegatedKeys(p => ({ ...p, [bq._key]: delegateTarget }));
    setDelegatingBQ(null); setDelegateTarget("");
  }

  // All hooks must be called before any conditional returns (React rules of hooks)
  const numQ = session?.numQ || 0;
  const answerKey = session?.answerKey || [];
  const numOptions = session?.numOptions || 4;
  const questions = session?.questions || [];
  const LABELS = ALL_LABELS.slice(0, numOptions);
  const maxScore = numQ * 4;
  // Poängordning för frågeanalysens topp-/bottengrupper
  const sortedResults = useMemo(() => [...results].sort((a, b) => b.total - a.total), [results]);
  // Gruppaktivitet: namnordning, så att man hittar en grupp och raderna står still
  const resultsByName = useMemo(() => [...results].sort((a, b) => compareGroupNames(a.groupName, b.groupName)), [results]);
  // Poängtabellen: klara grupper efter poäng, pågående sist (deras summa är
  // ännu inte jämförbar); lika poäng i namnordning
  const resultsByScore = useMemo(() => [...results].sort((a, b) =>
    (a.inProgress === true) - (b.inProgress === true)
    || b.total - a.total
    || compareGroupNames(a.groupName, b.groupName)), [results]);
  const { topGroup, bottomGroup, nGroups, groupSize } = useMemo(() => {
    const n = results.length;
    const gs = n >= 4 ? Math.max(1, Math.round(n * 0.27)) : Math.max(1, Math.floor(n / 2));
    return { nGroups: n, groupSize: gs, topGroup: sortedResults.slice(0, gs), bottomGroup: sortedResults.slice(-gs) };
  }, [results, sortedResults]);
  const qStats = useMemo(() => Array.from({ length: numQ }, (_, qi) => {
    const topCorrect = topGroup.filter(r => (r.scores?.[qi] ?? 0) > 0).length;
    const bottomCorrect = bottomGroup.filter(r => (r.scores?.[qi] ?? 0) > 0).length;
    const disc = groupSize > 0 ? (topCorrect - bottomCorrect) / groupSize : 0;
    return {
      qi, disc,
      avgScore: nGroups > 0 ? results.reduce((s, r) => s + (r.scores?.[qi] ?? 0), 0) / nGroups : 0,
      firstTries: results.filter(r => r.scores?.[qi] === 4).length,
      perGroup: results.map(r => ({ name: r.groupName, score: r.scores?.[qi] ?? null })),
      attemptDist: {
        first:  results.filter(r => (r.attempts?.[qi] ?? 0) === 1).length,
        second: results.filter(r => (r.attempts?.[qi] ?? 0) === 2).length,
        third:  results.filter(r => (r.attempts?.[qi] ?? 0) === 3).length,
        miss:   results.filter(r => (r.attempts?.[qi] ?? 0) >= 4).length,
      },
      optionDist: Array.from({ length: numOptions }, (_, oi) =>
        results.filter(r => (r.scratchState?.[qi]?.attempts ?? [])[0] === oi).length
      ),
    };
  }), [results, numQ, numOptions, topGroup, bottomGroup, nGroups, groupSize]);
  const pendingAppeals = useMemo(() => appeals.filter(a => !(appealStatuses[a._key]?.status) && a.status === "pending").length, [appeals, appealStatuses]);
  const hasRationale = questions.some(q => q?.rationale?.trim());

  if (loading) return <div style={{ ...css.page, display: "flex", alignItems: "center", justifyContent: "center", color: G.muted }}>{t.loading}</div>;
  if (err || !session) return <div style={{ ...css.page, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 20 }}><div style={{ color: G.red }}>{err}</div><button style={{ ...css.btnSecondary, width: "auto" }} onClick={onBack}>{t.back}</button></div>;

  const TabBtn = ({ id, label, badge }) => (
    <button onClick={() => {
      setTab(id);
      if (id === "appeals") { setLastSeenAppeals(Date.now()); setUnseenAppeals(0); }
      if (id === "bq") { setLastSeenBQs(Date.now()); setUnseenBQs(0); }
    }} style={{ position: "relative", background: tab === id ? G.gold : "transparent", color: tab === id ? G.bg : G.muted, border: `1px solid ${tab === id ? G.gold : G.border}`, borderRadius: 8, padding: "7px 16px", fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: tab === id ? 600 : 400, cursor: "pointer", transition: "all 0.2s" }}>
      {label}
      {badge > 0 && <span style={{ position: "absolute", top: -6, right: -6, background: G.red, color: "#fff", borderRadius: 999, fontSize: 10, fontWeight: 700, padding: "1px 5px", minWidth: 16, textAlign: "center", lineHeight: "14px" }}>{badge}</span>}
    </button>
  );

  const totalInbox = pendingAppeals + bqs.length;
  const inboxParts = [];
  if (pendingAppeals > 0) inboxParts.push({ label: lang === "sv" ? `${pendingAppeals} appeal${pendingAppeals === 1 ? "" : "s"}` : `${pendingAppeals} appeal${pendingAppeals === 1 ? "" : "s"}`, color: G.greenAppeal, target: "appeals" });
  if (bqs.length > 0) inboxParts.push({ label: lang === "sv" ? `${bqs.length} burning question${bqs.length === 1 ? "" : "s"}` : `${bqs.length} burning question${bqs.length === 1 ? "" : "s"}`, color: G.gold, target: "bq" });
  const primaryInboxTarget = pendingAppeals > 0 ? "appeals" : bqs.length > 0 ? "bq" : null;

  return (
    <div>
      <ServerTroubleBanner trouble={serverTrouble} audience="teacher" lang={lang} G={G} />
      {/* ═══ MASTHEAD — informational, no actions ═══ */}
      <div style={{ background: G.surface, borderBottom: `1px solid ${G.border}`, padding: "20px 28px 16px 44px" }}>
        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: G.muted, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>
          <span style={{ marginRight: 14 }}>ID · {session.sessionId}</span>
          <span style={{ marginRight: 14 }}>{results.length} {lang === "sv" ? "grupper" : "groups"}</span>
          {(() => {
            // Shared status model — "Avslutad" here used to mean "locked" while
            // it meant "has finished groups" on Översikt
            const meta = statusMeta(sessionStatus({
              locked: sessionLocked,
              availableAt: session.availableAt,
              activeGroups: results.filter(r => r.inProgress).length,
              doneGroups: results.filter(r => !r.inProgress).length,
            }), lang, G);
            return <span style={{ color: meta.color, fontWeight: 600 }}>{meta.label}</span>;
          })()}
        </div>
        <h1 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 26, fontWeight: 700, color: G.text, margin: 0, letterSpacing: "-0.01em" }}>
          {session.sessionTitle || session.sessionId}
        </h1>
      </div>

      {/* ═══ TOOLS ROW — timer (left), session controls + primary action (right) ═══ */}
      <div style={{ background: G.surface, borderBottom: `1px solid ${G.border}`, padding: "10px 28px 10px 44px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, position: "sticky", top: 52, zIndex: 18 }}>
        {/* LEFT: Timer */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: G.surface2, border: `1px solid ${timerExpired ? G.red : timerEnd ? G.gold : G.border}`, borderRadius: 8, padding: "4px 10px" }}>
          {timerEnd ? (
            <>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, fontWeight: 700, color: timerExpired ? G.red : timerMins < 2 ? G.gold : G.text, minWidth: 42 }}>
                {timerExpired ? t.timerDone : `${timerMins}:${String(timerSecs).padStart(2, "0")}`}
              </span>
              <button onClick={() => addTime(5)} style={{ background: G.accent + "18", border: `1px solid ${G.accent}44`, borderRadius: 5, color: G.accent, fontSize: 11, fontWeight: 700, cursor: "pointer", padding: "2px 6px", whiteSpace: "nowrap" }}>+5</button>
              <button onClick={stopTimer} style={{ background: "none", border: "none", color: G.muted, fontSize: 12, cursor: "pointer", padding: "0 2px" }}>✕</button>
              {timerErr && <span role="alert" style={{ fontSize: 12, color: G.red, fontWeight: 600 }}>{lang === "sv" ? "Kunde inte förlänga – försök igen" : "Could not extend – try again"}</span>}
            </>
          ) : (
            <>
              <style dangerouslySetInnerHTML={{ __html: `input.timer-input::-webkit-inner-spin-button, input.timer-input::-webkit-outer-spin-button { opacity: 1; filter: ${theme === 'dark' ? 'invert(0.85)' : 'none'}; }` }} />
              <input type="number" min={1} max={90} value={timerDuration} onChange={e => setTimerDuration(Math.max(1, Math.min(90, parseInt(e.target.value) || 10)))}
                className="timer-input"
                style={{ background: "none", border: "none", color: G.text, fontFamily: "'DM Mono',monospace", fontSize: 14, width: 40, outline: "none", textAlign: "right", paddingRight: 2 }} />
              <span style={{ fontSize: 12, color: G.muted }}>{t.timerMinutes}</span>
              <button onClick={startTimer} style={{ background: G.gold + "22", border: `1px solid ${G.gold}44`, borderRadius: 6, color: G.gold, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "2px 8px" }}>{t.timerStart}</button>
            </>
          )}
        </div>

        {/* RIGHT: det som används under lektionen ligger synligt (resultattavla,
            gruppkoder). Export och uppdatera ligger i menyn, och "Avsluta tRAT"
            — en engångsåtgärd som stänger allt skrapande — ligger sist i menyn i
            stället för som egen knapp bredvid timern. Körläget är borttaget: det
            dubblerade Gruppaktivitet och Live resultattavlan. */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {session.leaderboardId && (
            <button onClick={() => window.open(`${window.location.origin}/?lb=${session.leaderboardId}`, '_blank')}
              style={{ background: "transparent", border: `1px solid ${G.border}`, borderRadius: 8, color: G.text, fontSize: 13, fontWeight: 500, padding: "6px 14px", cursor: "pointer", whiteSpace: "nowrap" }}>
              {t.liveLeaderboard} ↗
            </button>
          )}
          {session?.config?.group_codes && Object.keys(session.config.group_codes).length > 0 && (
            <button onClick={() => setShowGroupCodes(p => !p)}
              style={{ background: showGroupCodes ? G.gold + "18" : "transparent", border: `1px solid ${showGroupCodes ? G.gold + "66" : G.border}`, borderRadius: 8, color: showGroupCodes ? G.gold : G.text, fontSize: 13, fontWeight: 500, padding: "6px 14px", cursor: "pointer", whiteSpace: "nowrap" }}>
              {lang === "sv" ? (showGroupCodes ? "Dölj gruppkoder" : "Gruppkoder") : (showGroupCodes ? "Hide group codes" : "Group codes")}
            </button>
          )}
          <button onClick={() => setShowQuestions(true)}
            style={{ background: "transparent", border: `1px solid ${G.border}`, borderRadius: 8, color: G.text, fontSize: 13, fontWeight: 500, padding: "6px 14px", cursor: "pointer", whiteSpace: "nowrap" }}>
            {t.showQuestions}
          </button>

          {/* More menu */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowMoreMenu(p => !p)}
              style={{ background: "transparent", border: `1px solid ${showMoreMenu ? G.gold + "66" : G.border}`, borderRadius: 8, color: showMoreMenu ? G.gold : G.muted, fontSize: 16, padding: "4px 12px", cursor: "pointer", lineHeight: 1.4 }}
              aria-label={lang === "sv" ? "Mer" : "More"}
            >
              ⋯
            </button>
            {showMoreMenu && (
              <>
                <div onClick={() => setShowMoreMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 49 }} />
                <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: 6, zIndex: 50, minWidth: 220, boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}>
                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: G.muted, letterSpacing: "0.14em", textTransform: "uppercase", padding: "8px 12px 4px" }}>
                    {lang === "sv" ? "Exportera" : "Export"}
                  </div>
                  <button onClick={() => { exportXLSX(results, numQ, session.sessionId); setShowMoreMenu(false); }}
                    style={{ display: "block", width: "100%", background: "transparent", border: "none", borderRadius: 7, color: G.text, fontSize: 13, padding: "8px 12px", cursor: "pointer", textAlign: "left", fontFamily: "'DM Sans',sans-serif" }}
                    onMouseEnter={e => e.currentTarget.style.background = G.surface2}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    Excel (.xlsx)
                  </button>
                  <button onClick={() => { exportPDF(results, numQ, session.sessionId, qStats, answerKey, LABELS); setShowMoreMenu(false); }}
                    style={{ display: "block", width: "100%", background: "transparent", border: "none", borderRadius: 7, color: G.text, fontSize: 13, padding: "8px 12px", cursor: "pointer", textAlign: "left", fontFamily: "'DM Sans',sans-serif" }}
                    onMouseEnter={e => e.currentTarget.style.background = G.surface2}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    PDF
                  </button>

                  <div style={{ height: 1, background: G.border, margin: "6px 0" }} />
                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: G.muted, letterSpacing: "0.14em", textTransform: "uppercase", padding: "4px 12px 4px" }}>
                    {lang === "sv" ? "Session" : "Session"}
                  </div>
                  {hasRationale && (
                  <button onClick={() => { toggleRationale(); setShowMoreMenu(false); }}
                    style={{ display: "block", width: "100%", background: "transparent", border: "none", borderRadius: 7, color: showRationale ? G.gold : G.text, fontSize: 13, padding: "8px 12px", cursor: "pointer", textAlign: "left", fontFamily: "'DM Sans',sans-serif" }}
                    onMouseEnter={e => e.currentTarget.style.background = G.surface2}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    {showRationale ? t.rationaleVisible : t.showRationale}
                  </button>
                  )}
                  <button onClick={() => { refresh(); setShowMoreMenu(false); }}
                    style={{ display: "block", width: "100%", background: "transparent", border: "none", borderRadius: 7, color: G.text, fontSize: 13, padding: "8px 12px", cursor: "pointer", textAlign: "left", fontFamily: "'DM Sans',sans-serif" }}
                    onMouseEnter={e => e.currentTarget.style.background = G.surface2}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    {t.refresh}
                  </button>

                  <div style={{ height: 1, background: G.border, margin: "6px 0" }} />
                  <button onClick={() => { requestToggleLock(); setShowMoreMenu(false); }}
                    style={{ display: "block", width: "100%", background: "transparent", border: "none", borderRadius: 7, color: sessionLocked ? G.text : G.red, fontSize: 13, padding: "8px 12px", cursor: "pointer", textAlign: "left", fontFamily: "'DM Sans',sans-serif" }}
                    onMouseEnter={e => e.currentTarget.style.background = G.surface2}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    {sessionLocked ? t.unlockSession : t.lockSession}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ═══ INKORG — always visible (collapsed empty state) ═══ */}
      {(
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "20px 28px 0" }}>
          <div style={{
            background: totalInbox > 0 ? G.gold + "0c" : "transparent",
            border: `1px solid ${totalInbox > 0 ? G.gold + "55" : G.border}`,
            borderRadius: 12,
            padding: totalInbox > 0 ? "14px 20px" : "10px 20px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 16, flexWrap: "wrap",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0, flex: 1 }}>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: totalInbox > 0 ? G.gold : G.muted, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600, flexShrink: 0 }}>
                {lang === "sv" ? "Inkorg" : "Inbox"}
              </div>
              <div style={{ width: 1, height: 16, background: G.border, flexShrink: 0 }} />
              <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center" }}>
                {totalInbox === 0 ? (
                  <span style={{ fontSize: 13, color: G.muted, fontStyle: "italic" }}>
                    {lang === "sv" ? "Inga åtgärder väntar" : "No pending actions"}
                  </span>
                ) : (
                  inboxParts.map(part => (
                    <span key={part.target} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: G.text }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: part.color, flexShrink: 0 }} />
                      <span style={{ fontWeight: 500 }}>{part.label}</span>
                    </span>
                  ))
                )}
              </div>
            </div>
            {totalInbox > 0 && (
              <button
                onClick={() => {
                  setTab(primaryInboxTarget);
                  if (primaryInboxTarget === "appeals") { setLastSeenAppeals(Date.now()); setUnseenAppeals(0); }
                  if (primaryInboxTarget === "bq") { setLastSeenBQs(Date.now()); setUnseenBQs(0); }
                }}
                style={{
                  background: G.gold, border: `1px solid ${G.gold}`,
                  borderRadius: 8, color: G.bg,
                  fontSize: 13, fontWeight: 600, padding: "6px 16px",
                  cursor: "pointer", flexShrink: 0,
                }}>
                {lang === "sv" ? "Hantera →" : "Handle →"}
              </button>
            )}
          </div>
        </div>
      )}

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 28px" }}>

        {/* ── LIVE MODE ── */}
        {(
        <>
        {/* ── NORMAL MODE ── */}
        {/* Editorial tabs — serif italic inactive, normal+underline active, dot for pending */}
        <div style={{ display: "flex", gap: 0, marginBottom: 28, borderBottom: `1px solid ${G.border}`, flexWrap: "wrap" }}>
          {[
            { id: "scores", label: t.groupScores, dot: 0 },
            { id: "appeals", label: t.appeals, dot: pendingAppeals, color: G.greenAppeal },
            { id: "bq", label: t.bqLabel, dot: bqs.length, color: G.gold },
            { id: "questions", label: t.questionAnalysis, dot: 0 },
            ...(delegations.length > 0 ? [{ id: "delegations", label: t.delegationAnswers, dot: delegations.length, color: G.accent }] : []),
          ].map(tabItem => {
            const active = tab === tabItem.id;
            return (
              <button
                key={tabItem.id}
                onClick={() => {
                  setTab(tabItem.id);
                  if (tabItem.id === "appeals") { setLastSeenAppeals(Date.now()); setUnseenAppeals(0); }
                  if (tabItem.id === "bq") { setLastSeenBQs(Date.now()); setUnseenBQs(0); }
                }}
                style={{
                  position: "relative",
                  background: "transparent",
                  color: active ? G.text : G.muted,
                  border: "none",
                  borderBottom: active ? `2px solid ${G.text}` : "2px solid transparent",
                  padding: "10px 18px",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: active ? 600 : 400,
                  fontStyle: active ? "normal" : "italic",
                  cursor: "pointer",
                  marginBottom: -1,
                  transition: "all 0.15s",
                  display: "inline-flex", alignItems: "center", gap: 8,
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.color = G.text; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.color = G.muted; }}
              >
                {tabItem.label}
                {tabItem.dot > 0 && (
                  <span style={{
                    minWidth: 18, height: 18, padding: "0 6px",
                    background: tabItem.color, color: G.bg,
                    borderRadius: 999,
                    fontSize: 10, fontWeight: 700,
                    fontFamily: "'DM Mono', monospace",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>{tabItem.dot}</span>
                )}
              </button>
            );
          })}
        </div>

        {showGroupCodes && session?.config?.group_codes && Object.keys(session.config.group_codes).length > 0 && (
          <div style={{ marginBottom: 24, background: G.surface, border: `1px solid ${G.gold + "44"}`, borderRadius: 12, padding: "14px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: G.gold, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600 }}>
                {lang === "sv" ? "Gruppkoder" : "Group codes"} · {Object.keys(session.config.group_codes).length}
              </div>
              <button onClick={() => setShowGroupCodes(false)} style={{ background: "none", border: "none", color: G.muted, fontSize: 14, cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button>
            </div>
            {/* Studenterna behöver båda: tRAT-koden för att hitta rätt tRAT, sedan gruppens kod */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${G.border}` }}>
              <span style={{ fontSize: 13, color: G.sub }}>{lang === "sv" ? "tRAT-kod" : "tRAT code"}</span>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 20, color: G.gold, fontWeight: 700, letterSpacing: "0.14em" }}>{session.sessionId}</span>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
              {Object.entries(session.config.group_codes).map(([group, code]) => (
                <span key={group} style={{ display: "inline-flex", alignItems: "center", gap: 10, fontSize: 13, color: G.sub, background: G.surface2, borderRadius: 6, padding: "5px 12px" }}>
                  <span style={{ fontWeight: 600, color: G.text }}>{group}</span>
                  <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 15, color: G.gold, fontWeight: 700, letterSpacing: "0.1em" }}>{code}</span>
                </span>
              ))}
            </div>
            <button onClick={() => openCodeSheet(session.sessionTitle, session.sessionId, session.config.group_codes)} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 12, padding: "4px 12px", cursor: "pointer" }}>
              {t.downloadCodeSheet}
            </button>
          </div>
        )}

        {tab === "scores" && results.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
              <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14, fontWeight: 600, color: G.text, margin: 0, letterSpacing: "-0.005em" }}>
                {lang === "sv" ? "Gruppaktivitet" : "Group activity"}
              </h3>
              <div style={{ display: "flex", gap: 14, fontFamily: "'DM Mono', monospace", fontSize: 10, color: G.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: G.correct }} />{lang === "sv" ? "Aktiv" : "Active"}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: G.gold }} />{lang === "sv" ? "Nyligen" : "Recent"}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: G.border }} />{lang === "sv" ? "Inaktiv" : "Idle"}
                </span>
              </div>
            </div>
            <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, overflow: "hidden" }}>
              {resultsByName.map((r, idx) => {
                const activity = activityStatus(r.updatedAt);
                const doneQs = r.scratchState ? r.scratchState.filter(q => q?.done).length : (r.scores ? r.scores.filter(s => s !== null && s !== undefined).length : 0);
                const allDone = doneQs >= numQ;
                // Avslutat med obesvarade frågor visades förut som "Frågar Q4" —
                // som om gruppen fortfarande satt och jobbade.
                const finishedEarly = !r.inProgress && !allDone;
                const hasAttempts = Array.isArray(r.scratchState) && r.scratchState.some(q => (q?.attempts?.length || 0) > 0);
                const notStarted = doneQs === 0 && !hasAttempts && r.inProgress;
                const pct = numQ > 0 ? doneQs / numQ : 0;
                const currentQ = r.scratchState ? (r.scratchState.findIndex(q => !q?.done) + 1) || numQ : doneQs + 1;
                const statusLabel = allDone
                  ? (lang === "sv" ? "Klar" : "Done")
                  : finishedEarly
                    ? (lang === "sv" ? `Klar (${doneQs}/${numQ})` : `Finished (${doneQs}/${numQ})`)
                    : notStarted
                      ? (lang === "sv" ? "Ej startat" : "Not started")
                      : (lang === "sv" ? `På fråga ${currentQ}` : `On question ${currentQ}`);
                const barColor = allDone ? G.correct : finishedEarly ? G.border : notStarted ? G.border : G.gold;
                return (
                  <div key={r.groupName} style={{
                    display: "grid", gridTemplateColumns: "1fr minmax(160px, 2fr) 110px",
                    alignItems: "center", gap: 16,
                    padding: "11px 18px",
                    borderBottom: idx < resultsByName.length - 1 ? `1px solid ${G.border}33` : "none",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: activityDotColor[activity], flexShrink: 0 }} />
                      <span style={{ fontSize: 14, fontWeight: 500, color: G.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.groupName}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ flex: 1, height: 6, background: G.border + "44", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{ width: `${pct * 100}%`, height: "100%", background: barColor, borderRadius: 999, transition: "width 0.3s" }} />
                      </div>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: G.muted, fontWeight: 500, minWidth: 36, textAlign: "right" }}>
                        {doneQs}/{numQ}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: allDone ? G.correct : notStarted ? G.muted : G.gold, fontWeight: allDone ? 600 : 400, textAlign: "right" }}>
                      {statusLabel}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "scores" && results.length > 0 && (
          <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14, fontWeight: 600, color: G.text, margin: "0 0 12px", letterSpacing: "-0.005em" }}>
            {t.groupScores}
          </h3>
        )}
        {tab === "scores" && (
          results.length === 0
            ? <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: 24, color: G.muted, textAlign: "center" }}>{t.noGroupsYet}</div>
            : <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, fontFamily: "'DM Mono',monospace", minWidth: Math.max(600, 160 + numQ * 48) }}>
                <thead><tr style={{ borderBottom: `1px solid ${G.border}` }}>
                  <th style={{ textAlign: "left", padding: "12px 16px", color: G.muted, fontWeight: 500 }}>{t.tableGroup}</th>
                  {Array.from({ length: numQ }, (_, i) => <th key={i} style={{ padding: "12px 8px", color: G.muted, fontWeight: 500, minWidth: 34 }}>Q{i + 1}</th>)}
                  <th style={{ padding: "12px 16px", color: G.gold, fontWeight: 600 }}>{t.totalScore}</th>
                </tr></thead>
                <tbody>
                  {resultsByScore.map((r, ri) => (
                    <tr key={r.groupName} style={{ borderBottom: `1px solid ${G.border}33`, background: ri % 2 === 0 ? "transparent" : G.bg + "88" }}>
                      <td style={{ padding: "10px 16px", fontFamily: "'DM Sans',sans-serif", fontWeight: 500, color: G.text, position: "relative" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 6, height: 6, borderRadius: "50%", background: activityDotColor[activityStatus(r.updatedAt)], flexShrink: 0 }} title={activityStatus(r.updatedAt) === 'active' ? (lang === 'sv' ? 'Aktiv' : 'Active') : activityStatus(r.updatedAt) === 'recent' ? (lang === 'sv' ? 'Nyligen aktiv' : 'Recently active') : (lang === 'sv' ? 'Inaktiv' : 'Idle')} />
                          {editingGroupName?.old === r.groupName ? (
                            <input
                              autoFocus
                              value={editingGroupName.value}
                              onChange={e => setEditingGroupName(p => ({ ...p, value: e.target.value }))}
                              onKeyDown={async e => {
                                if (e.key === "Enter" && renameAllowed(editingGroupName)) {
                                  await dbRenameGroup(session.sessionId, editingGroupName.old, editingGroupName.value.trim());
                                  setEditingGroupName(null); refresh();
                                } else if (e.key === "Escape") setEditingGroupName(null);
                              }}
                              onBlur={async () => {
                                if (renameAllowed(editingGroupName)) {
                                  await dbRenameGroup(session.sessionId, editingGroupName.old, editingGroupName.value.trim());
                                  refresh();
                                }
                                setEditingGroupName(null);
                              }}
                              style={{ background: G.bg, border: `1px solid ${G.gold}`, borderRadius: 4, color: G.text, fontSize: 14, padding: "2px 6px", fontFamily: "'DM Sans',sans-serif", outline: "none", width: 120 }}
                            />
                          ) : (
                            <>
                              {r.groupName}
                              {r.inProgress && <span style={{ fontSize: 10, color: G.gold, background: G.gold + "18", borderRadius: 999, padding: "1px 7px" }}>● pågår</span>}
                            </>
                          )}
                          <button
                            onClick={e => { e.stopPropagation(); setGroupMenu(groupMenu === r.groupName ? null : r.groupName); setDeleteConfirmGroup(null); }}
                            style={{ background: "none", border: "none", color: G.muted, cursor: "pointer", fontSize: 14, padding: "0 4px", opacity: 0.5, lineHeight: 1 }}
                            onMouseEnter={e => e.currentTarget.style.opacity = 1}
                            onMouseLeave={e => e.currentTarget.style.opacity = 0.5}
                          >···</button>
                        </div>
                        {groupMenu === r.groupName && (
                          <>
                            <div onClick={() => setGroupMenu(null)} style={{ position: "fixed", inset: 0, zIndex: 49 }} />
                            <div style={{ position: "absolute", left: 16, top: "100%", background: G.surface, border: `1px solid ${G.border}`, borderRadius: 8, padding: 4, zIndex: 50, boxShadow: "0 4px 16px rgba(0,0,0,0.2)", minWidth: 140 }}>
                              {/* Gruppens enheter känner igen gruppen på namnet. Byts
                                  det medan tRAT:en är öppen tappar de kontakten, så
                                  namnbyte går bara när tRAT:en är avslutad. */}
                              <button
                                disabled={!sessionLocked}
                                title={!sessionLocked ? (lang === "sv" ? "Avsluta tRAT:en först – annars tappar gruppens enheter kontakten" : "End the tRAT first – otherwise the group's devices lose contact") : undefined}
                                onClick={() => { if (!sessionLocked) return; setEditingGroupName({ old: r.groupName, value: r.groupName }); setGroupMenu(null); }}
                                style={{ display: "block", width: "100%", background: "transparent", border: "none", borderRadius: 6, color: sessionLocked ? G.text : G.muted, fontSize: 13, padding: "7px 12px", cursor: sessionLocked ? "pointer" : "not-allowed", textAlign: "left", fontFamily: "'DM Sans',sans-serif" }}
                                onMouseEnter={e => { if (sessionLocked) e.currentTarget.style.background = G.surface2; }}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                              >
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}><Pencil size={13} /> {lang === "sv" ? "Byt namn" : "Rename"}</span>
                                {!sessionLocked && <span style={{ display: "block", fontSize: 11, color: G.muted, marginTop: 2 }}>{lang === "sv" ? "Går när tRAT:en är avslutad" : "Available once the tRAT has ended"}</span>}
                              </button>
                              {deleteConfirmGroup === r.groupName ? (
                                <div style={{ padding: "6px 12px" }}>
                                  <div style={{ fontSize: 12, color: G.red, marginBottom: 6 }}>{lang === "sv" ? "Säker? Kan ej ångras." : "Sure? Cannot be undone."}</div>
                                  <div style={{ display: "flex", gap: 6 }}>
                                    <button onClick={async () => { await dbDeleteGroup(session.sessionId, r.groupName); setGroupMenu(null); setDeleteConfirmGroup(null); refresh(); }}
                                      style={{ background: G.red, border: "none", borderRadius: 4, color: "#fff", fontSize: 12, fontWeight: 600, padding: "4px 10px", cursor: "pointer" }}>{lang === "sv" ? "Ta bort" : "Delete"}</button>
                                    <button onClick={() => setDeleteConfirmGroup(null)}
                                      style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 4, color: G.muted, fontSize: 12, padding: "4px 10px", cursor: "pointer" }}>{lang === "sv" ? "Avbryt" : "Cancel"}</button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setDeleteConfirmGroup(r.groupName)}
                                  style={{ display: "block", width: "100%", background: "transparent", border: "none", borderRadius: 6, color: G.red, fontSize: 13, padding: "7px 12px", cursor: "pointer", textAlign: "left", fontFamily: "'DM Sans',sans-serif" }}
                                  onMouseEnter={e => e.currentTarget.style.background = G.red + "11"}
                                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                >
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}><Trash2 size={13} /> {lang === "sv" ? "Ta bort grupp" : "Delete group"}</span>
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </td>
                      {Array.from({ length: numQ }, (_, qi) => { const sc = r.scores?.[qi] ?? null; const col = sc === null ? G.muted : sc === 4 ? G.correct : sc === 2 ? G.gold : sc === 1 ? G.goldDim : G.red; return <td key={qi} style={{ padding: "10px 8px", textAlign: "center", color: col, fontWeight: sc === 4 ? 600 : 400 }}>{sc ?? "—"}</td>; })}
                      <td style={{ padding: "10px 16px", textAlign: "center", color: scoreColor(r.total / maxScore, G), fontWeight: 700, fontSize: 15 }}>{r.total}</td>
                    </tr>
                  ))}
                  {results.length > 1 && (
                    <tr style={{ borderTop: `2px solid ${G.border}`, background: G.surface2 }}>
                      <td style={{ padding: "10px 16px", color: G.muted, fontFamily: "'DM Sans',sans-serif", fontSize: 13 }}>{t.tableAverage}</td>
                      {Array.from({ length: numQ }, (_, qi) => { const avg = results.reduce((s, r) => s + (r.scores?.[qi] ?? 0), 0) / results.length; return <td key={qi} style={{ padding: "10px 8px", textAlign: "center", color: scoreColor(avg / 4, G) }}>{avg.toFixed(1)}</td>; })}
                      <td style={{ padding: "10px 16px", textAlign: "center", color: G.gold, fontWeight: 700 }}>{(results.reduce((s, r) => s + r.total, 0) / results.length).toFixed(1)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
        )}

        {tab === "questions" && (
          <>
            <p style={{ color: G.muted, fontSize: 14, marginBottom: 20, lineHeight: 1.6, maxWidth: 600 }}>{t.questionAnalysisSub}</p>

            {/* Editorial table — ruled lines, no card wrapper */}
            <div>
              {/* Header row — top + bottom rule, mono uppercase */}
              <div style={{
                display: "grid", gridTemplateColumns: "44px 1fr 96px 64px 110px 28px",
                gap: 18, alignItems: "center",
                padding: "12px 4px",
                borderTop: `2px solid ${G.text}`,
                borderBottom: `1px solid ${G.border}`,
                fontFamily: "'DM Mono', monospace",
                fontSize: 10, fontWeight: 600,
                color: G.muted,
                letterSpacing: "0.14em", textTransform: "uppercase",
              }}>
                <div>Q</div>
                <div>{lang === "sv" ? "Fråga" : "Question"}</div>
                <div style={{ textAlign: "right" }}>{lang === "sv" ? "Snitt" : "Avg"}</div>
                <div style={{ textAlign: "center" }}>{lang === "sv" ? "Facit" : "Key"}</div>
                <div style={{ textAlign: "right" }}>{lang === "sv" ? "Svårighet" : "Difficulty"}</div>
                <div></div>
              </div>

              {/* Question rows */}
              {qStats.map(({ qi, avgScore, firstTries, perGroup, attemptDist, optionDist, disc }) => {
                const n = results.length;
                const difficulty = avgScore / 4;
                const rowColor = difficulty < 0.4 ? G.red : difficulty < 0.65 ? G.gold : G.correct;
                const discColor = disc >= 0.3 ? G.correct : disc >= 0.1 ? G.gold : G.red;
                const discLabel = disc >= 0.3 ? t.discGood : disc >= 0.1 ? t.discFair : t.discPoor;
                const qData = questions[qi] || {};
                const distTotal = attemptDist.first + attemptDist.second + attemptDist.third + attemptDist.miss;
                const isExpanded = expandedQRow === qi;
                const qText = qData.text || "";
                const firstTriesPct = n > 0 ? Math.round(firstTries / n * 100) : 0;

                // Editorial difficulty label
                const diffBadge = n === 0
                  ? { label: "—", color: G.muted }
                  : difficulty < 0.4
                    ? { label: lang === "sv" ? "Svår" : "Hard", color: G.red }
                    : difficulty < 0.65
                      ? { label: lang === "sv" ? "Medel" : "Medium", color: G.gold }
                      : { label: lang === "sv" ? "Lätt" : "Easy", color: G.correct };

                return (
                  <div key={qi}>
                    {/* Main row — clickable */}
                    <div
                      onClick={() => setExpandedQRow(isExpanded ? null : qi)}
                      style={{
                        display: "grid", gridTemplateColumns: "44px 1fr 96px 64px 110px 28px",
                        gap: 18, alignItems: "center",
                        padding: "20px 4px",
                        borderBottom: `1px solid ${G.border}`,
                        cursor: "pointer",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = G.surface2 + "60"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      {/* Q number */}
                      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 500, color: G.muted, letterSpacing: "0.05em" }}>
                        {String(qi + 1).padStart(2, "0")}
                      </div>

                      {/* Question text + secondary line */}
                      <div style={{ minWidth: 0 }}>
                        <div style={{
                          fontSize: 15, color: G.text, fontWeight: 500, lineHeight: 1.45,
                          overflow: "hidden", display: "-webkit-box",
                          WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                        }}>
                          {qText ? <RichText text={qText} /> : <span style={{ color: G.muted, fontStyle: "italic", fontWeight: 400 }}>{lang === "sv" ? "Ingen frågetext" : "No question text"}</span>}
                        </div>
                        {n > 0 && (
                          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: G.muted, marginTop: 6, letterSpacing: "0.03em" }}>
                            {firstTriesPct}% {lang === "sv" ? "första försök" : "first try"}
                            {distTotal > 0 && (
                              <span> · {n} {lang === "sv" ? (n === 1 ? "grupp" : "grupper") : (n === 1 ? "group" : "groups")}</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Avg score — large number, small denominator */}
                      <div style={{ textAlign: "right" }}>
                        <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 22, fontWeight: 600, color: rowColor, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
                          {n > 0 ? avgScore.toFixed(1) : "—"}
                        </span>
                        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: G.muted, marginLeft: 3 }}>/4</span>
                      </div>

                      {/* Facit */}
                      <div style={{ textAlign: "center" }}>
                        <span style={{
                          display: "inline-block",
                          fontFamily: "'DM Mono', monospace",
                          fontSize: 14, fontWeight: 600, color: G.gold,
                          minWidth: 26, padding: "3px 0",
                          borderTop: `1px solid ${G.gold}66`,
                          borderBottom: `1px solid ${G.gold}66`,
                          letterSpacing: "0.05em",
                        }}>
                          {LABELS[answerKey[qi]]}
                        </span>
                      </div>

                      {/* Difficulty badge */}
                      <div style={{ textAlign: "right", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
                        <div style={{ width: 40, height: 4, background: G.border + "55", borderRadius: 999, overflow: "hidden" }}>
                          <div style={{ width: `${difficulty * 100}%`, height: "100%", background: diffBadge.color, borderRadius: 999 }} />
                        </div>
                        <span style={{
                          fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 600,
                          color: diffBadge.color, letterSpacing: "0.12em", textTransform: "uppercase",
                          minWidth: 42, textAlign: "right",
                        }}>
                          {diffBadge.label}
                        </span>
                      </div>

                      {/* Expand chevron */}
                      <div style={{ textAlign: "center", color: G.muted, fontSize: 11, transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>▼</div>
                    </div>

                    {/* Expanded panel */}
                    {isExpanded && (
                      <div style={{ padding: "4px 24px 24px", borderTop: `1px solid ${G.border}33` }}>

                        {/* Question text (full) */}
                        {qData.text && (
                          <QuestionText text={qData.text} style={{ fontSize: 15, color: G.text, marginBottom: 20, fontWeight: 500, lineHeight: 1.6, paddingTop: 12 }} />
                        )}
                        {qData.images?.length > 0 && (
                          <div style={{ maxWidth: 520, marginBottom: 20 }}>
                            <QuestionImages images={qData.images} G={G} lang={lang} maxHeight={320} />
                          </div>
                        )}

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 16 }}>

                          {/* Attempt distribution */}
                          {distTotal > 0 && (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: G.muted, marginBottom: 10 }}>{lang === 'sv' ? "Försöksfördelning" : "Attempt distribution"}</div>
                              <div style={{ display: "flex", height: 8, borderRadius: 999, overflow: "hidden", gap: 1, marginBottom: 8 }}>
                                {attemptDist.first  > 0 && <div style={{ flex: attemptDist.first,  background: G.accent }} />}
                                {attemptDist.second > 0 && <div style={{ flex: attemptDist.second, background: G.gold }} />}
                                {attemptDist.third  > 0 && <div style={{ flex: attemptDist.third,  background: G.goldDim }} />}
                                {attemptDist.miss   > 0 && <div style={{ flex: attemptDist.miss,   background: G.red }} />}
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                {[
                                  { count: attemptDist.first,  color: G.accent,   label: lang === 'sv' ? "1:a försök" : "1st try" },
                                  { count: attemptDist.second, color: G.gold,    label: lang === 'sv' ? "2:a försök" : "2nd try" },
                                  { count: attemptDist.third,  color: G.goldDim, label: lang === 'sv' ? "3:e försök" : "3rd try" },
                                  { count: attemptDist.miss,   color: G.red,     label: lang === 'sv' ? "Missade" : "Missed" },
                                ].filter(d => d.count > 0).map(d => (
                                  <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: d.color, flexShrink: 0 }} />
                                    <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, color: G.text, fontWeight: 600, minWidth: 20 }}>{d.count}</span>
                                    <span style={{ fontSize: 12, color: G.muted }}>{d.label}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                        </div>

                        {/* Response distribution */}
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: G.muted, marginBottom: 10 }}>{lang === 'sv' ? "Svarsfördelning (1:a val)" : "Response distribution (1st choice)"}</div>
                          {LABELS.map((lbl, oi) => {
                            const count = optionDist?.[oi] ?? 0;
                            const maxCount = Math.max(...(optionDist || [1]), 1);
                            const isCorrect = oi === answerKey[qi];
                            return (
                              <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 7 }}>
                                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, color: isCorrect ? G.correct : G.muted, fontWeight: 700, minWidth: 28, textAlign: "right" }}>
                                  {lbl}{isCorrect ? " ✓" : ""}
                                </span>
                                <div style={{ flex: 1, maxWidth: 180, background: G.border + "44", borderRadius: 3, height: 10, overflow: "hidden" }}>
                                  <div style={{ width: `${(count / maxCount) * 100}%`, height: "100%", background: isCorrect ? G.correct : G.border, borderRadius: 3, transition: "width 0.3s" }} />
                                </div>
                                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, color: isCorrect ? G.correct : G.muted, minWidth: 20, fontWeight: 600 }}>{count}</span>
                                {qData.options?.[lbl] && <span style={{ fontSize: 13, color: G.sub, flex: 1 }}>{qData.options[lbl]}</span>}
                              </div>
                            );
                          })}
                        </div>

                        {/* Rationale */}
                        {qData.rationale?.trim() && (
                          <div style={{ fontSize: 13, color: G.sub, lineHeight: 1.6, background: G.bg, borderRadius: 8, padding: "10px 14px", borderLeft: `3px solid ${G.gold}66`, marginBottom: 14 }}>
                            <QuestionText text={qData.rationale} />
                          </div>
                        )}

                        {/* Per-group scores */}
                        {n > 0 && (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: G.muted, marginBottom: 8 }}>{lang === 'sv' ? "Poäng per grupp" : "Score per group"}</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                              {perGroup.map(g => (
                                <div key={g.name} style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, background: G.bg, borderRadius: 8, padding: "4px 10px", border: `1px solid ${G.border}`, display: "flex", gap: 8, alignItems: "center" }}>
                                  <span style={{ color: G.muted }}>{g.name}</span>
                                  <span style={{ fontWeight: 700, color: g.score === 4 ? G.correct : g.score === 2 ? G.gold : g.score === 1 ? G.goldDim : g.score === 0 ? G.red : G.muted }}>{g.score ?? "—"}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {tab === "appeals" && (
          appeals.length === 0
            ? <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: 24, color: G.muted, textAlign: "center" }}>{t.noAppeals}</div>
            : appeals.map((appeal, i) => {
              const statusObj = appealStatuses[appeal._key] || { status: appeal.status || "pending" };
              const typeColor = appeal.type === "green" ? G.greenAppeal : G.yellowAppeal;
              const isPending = statusObj.status === "pending";
              const groupResult = results.find(r => r.groupName === appeal.groupName);
              const ctx = appealContext({ result: groupResult, questionIndex: appeal.questionIndex, correctIndex: answerKey[appeal.questionIndex], proposedAnswer: appeal.type === "yellow" ? appeal.fields?.proposedAnswer : null, labels: LABELS });
              const isDeciding = isPending && deciding?.key === appeal._key;
              const choices = deciding?.kind === "approve" ? APPROVE_SCORES : REJECT_DEDUCTIONS;
              return (
                <div key={i} style={{ background: G.surface, border: `2px solid ${typeColor}44`, borderRadius: 12, padding: "20px 24px", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12, gap: 12 }}>
                    <div style={{ display: "flex", gap: 10 }}>
                      <span>{appeal.type === "green" ? "🟢" : "🟡"}</span>
                      <div><div style={{ fontWeight: 600, fontSize: 14 }}>{appeal.groupName}</div><div style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, color: G.muted }}>IFAT Q{appeal.iFATQuestion}</div></div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      {isPending
                        ? !isDeciding && <>
                            <button onClick={() => setDeciding({ key: appeal._key, kind: "approve", value: ctx.suggested })} style={{ background: G.accent + "22", border: `1px solid ${G.accent}44`, borderRadius: 8, color: G.accent, fontSize: 13, padding: "5px 12px", cursor: "pointer" }}>{t.approve}</button>
                            <button onClick={() => setDeciding({ key: appeal._key, kind: "reject", value: 0 })} style={{ background: G.red + "22", border: `1px solid ${G.red}44`, borderRadius: 8, color: G.red, fontSize: 13, padding: "5px 12px", cursor: "pointer" }}>{t.reject}</button>
                          </>
                        : <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, color: statusObj.status === "approved" ? G.accent : G.red, background: (statusObj.status === "approved" ? G.accent : G.red) + "18", borderRadius: 999, padding: "4px 12px" }}>{statusObj.status === "approved" ? t.appealApproved : t.appealRejected}{decisionPoints(statusObj.status, statusObj.adjustedScore ?? appeal.adjusted_score) ? ` · ${decisionPoints(statusObj.status, statusObj.adjustedScore ?? appeal.adjusted_score)}` : ""}</span>
                      }
                    </div>
                  </div>
                  {/* Hur gruppen svarade: grunden för hur många poäng appealen ger */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12, fontSize: 13 }}>
                    <span style={{ ...css.label, display: "inline", marginBottom: 0, fontSize: 10 }}>{t.appealGroupAnswers}</span>
                    {ctx.attempts.length === 0
                      ? <span style={{ color: G.muted }}>{t.appealNotAnswered}</span>
                      : <span aria-label={`${t.appealGroupAnswers}: ${ctx.attempts.map(a => `${a.label} ${a.correct ? t.appealRightShort : t.appealWrongShort}`).join(", ")}`} style={{ fontFamily: "'DM Mono',monospace", display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {ctx.attempts.map((a, ai) => (
                            <span key={ai} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              {ai > 0 && <span aria-hidden="true" style={{ color: G.muted }}>→</span>}
                              <span aria-hidden="true" style={{ fontWeight: 700, color: a.correct ? G.correct : G.red, background: (a.correct ? G.correct : G.red) + "18", borderRadius: 6, padding: "1px 7px" }}>{a.label} {a.correct ? "✓" : "✗"}</span>
                            </span>
                          ))}
                        </span>}
                    {ctx.currentScore !== null && <span style={{ color: G.muted }}>· {t.appealCurrentScore.replace("{n}", ctx.currentScore)}</span>}
                  </div>
                  {isDeciding && (
                    <div role="group" aria-label={deciding.kind === "approve" ? t.appealNewScore : t.appealDeduction} style={{ background: (deciding.kind === "approve" ? G.accent : G.red) + "10", border: `1px solid ${(deciding.kind === "approve" ? G.accent : G.red)}33`, borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: G.text, marginBottom: 8 }}>
                        {deciding.kind === "approve" ? t.appealNewScore : t.appealDeduction}
                        {deciding.kind === "approve" && ctx.suggested !== null && (
                          <span style={{ fontWeight: 400, color: G.muted }}> · {t.appealSuggestion.replace("{label}", LABELS[ctx.proposedIndex]).replace("{n}", ctx.suggestedTry).replace("{p}", ctx.suggested)}</span>
                        )}
                        {deciding.kind === "reject" && deciding.value < 0 && ctx.currentScore !== null && (
                          <span style={{ fontWeight: 400, color: G.muted }}> · {t.appealScoreAfter.replace("{n}", scoreAfterDeduction(ctx.currentScore, deciding.value))}</span>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                        {choices.map(v => {
                          const on = deciding.value === v;
                          const col = deciding.kind === "approve" ? G.accent : G.red;
                          return (
                            <button key={v} type="button" aria-pressed={on} onClick={() => setDeciding(d => ({ ...d, value: v }))}
                              style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, fontWeight: 600, minWidth: 44, padding: "5px 10px", borderRadius: 8, cursor: "pointer", border: `1.5px solid ${on ? col : G.border}`, background: on ? col + "22" : G.surface, color: on ? col : G.text }}>
                              {String(v).replace("-", "−")}
                            </button>
                          );
                        })}
                        <span style={{ fontSize: 13, color: G.muted, marginLeft: 2 }}>{t.points}</span>
                        <span style={{ flex: 1 }} />
                        <button onClick={() => setDeciding(null)} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 8, color: G.muted, fontSize: 13, padding: "5px 12px", cursor: "pointer" }}>{t.cancel}</button>
                        <button onClick={() => handleAppealDecision(appeal, deciding.kind, deciding.value)} disabled={deciding.value === null || deciding.value === undefined}
                          style={{ background: deciding.kind === "approve" ? G.accent : G.red, border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, padding: "6px 14px", cursor: "pointer", opacity: deciding.value === null || deciding.value === undefined ? 0.5 : 1 }}>
                          {deciding.kind === "approve" ? t.appealConfirmApprove : t.appealConfirmReject}
                        </button>
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                    {appeal.type === "green" && <>{appeal.fields?.ambiguity && <div style={{ fontSize: 14 }}><span style={{ ...css.label, display: "inline", marginBottom: 0, fontSize: 10 }}>{t.appealLabelAmbiguity}: </span>{appeal.fields.ambiguity}</div>}{appeal.fields?.impact && <div style={{ fontSize: 14 }}><span style={{ ...css.label, display: "inline", marginBottom: 0, fontSize: 10 }}>{t.appealLabelImpact}: </span>{appeal.fields.impact}</div>}{appeal.fields?.reasoning && <div style={{ fontSize: 14 }}><span style={{ ...css.label, display: "inline", marginBottom: 0, fontSize: 10 }}>{t.appealLabelReasoning}: </span>{appeal.fields.reasoning}</div>}{appeal.fields?.rewrite && <div style={{ fontSize: 14 }}><span style={{ ...css.label, display: "inline", marginBottom: 0, fontSize: 10 }}>{t.appealLabelRewrite}: </span>{appeal.fields.rewrite}</div>}</>}
                    {appeal.type === "yellow" && <>{appeal.fields?.proposedAnswer && <div style={{ fontSize: 14 }}><span style={{ ...css.label, display: "inline", marginBottom: 0, fontSize: 10 }}>{t.appealLabelProposed}: </span><span style={{ color: G.gold, fontWeight: 600 }}>{appeal.fields.proposedAnswer}</span></div>}{appeal.fields?.disagree && <div style={{ fontSize: 14 }}><span style={{ ...css.label, display: "inline", marginBottom: 0, fontSize: 10 }}>{t.appealLabelDisagreement}: </span>{appeal.fields.disagree}</div>}{appeal.fields?.reference && <div style={{ fontSize: 14 }}><span style={{ ...css.label, display: "inline", marginBottom: 0, fontSize: 10 }}>{t.appealLabelReferences}: </span>{appeal.fields.reference}</div>}{appeal.fields?.whyBest && <div style={{ fontSize: 14 }}><span style={{ ...css.label, display: "inline", marginBottom: 0, fontSize: 10 }}>{t.appealLabelWhyBest}: </span>{appeal.fields.whyBest}</div>}</>}
                  </div>
                  {/* Question preview toggle */}
                  <div style={{ marginTop: 10 }}>
                    <button onClick={() => setExpandedAppealQ(expandedAppealQ === appeal._key ? null : appeal._key)} style={{ background: "none", border: `1px solid ${expandedAppealQ === appeal._key ? G.gold : G.border}`, borderRadius: 7, color: expandedAppealQ === appeal._key ? G.gold : G.muted, fontSize: 12, padding: "3px 10px", cursor: "pointer" }}>
                      {expandedAppealQ === appeal._key ? t.hideQuestion : t.showQuestion}
                    </button>
                  </div>
                  {expandedAppealQ === appeal._key && (() => {
                    const qi = (appeal.iFATQuestion || 1) - 1;
                    const qData = questions[qi];
                    const correctIdx = answerKey?.[qi];
                    if (!qData) return null;
                    return (
                      <div style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 12, padding: "12px 16px", marginTop: 8 }}>
                        {qData.text && <QuestionText text={qData.text} style={{ fontSize: 14, color: G.text, marginBottom: 8, fontWeight: 500 }} />}
                        {LABELS.map((lbl, oi) => (
                          <div key={lbl} style={{ fontSize: 13, color: oi === correctIdx ? G.correct : G.muted, marginBottom: 3 }}>
                            <strong style={{ fontFamily: "'DM Mono',monospace" }}>{lbl}{oi === correctIdx ? " ✓" : ""}</strong>
                            {qData.options?.[lbl] ? ` — ${qData.options[lbl]}` : ""}
                          </div>
                        ))}
                        {qData.rationale && <div style={{ fontSize: 13, color: G.goldDim, marginTop: 8, fontStyle: "italic" }}>💡 <RichText text={qData.rationale} /></div>}
                      </div>
                    );
                  })()}
                </div>
              );
            })
        )}

        {tab === "bq" && (
          bqs.length === 0
            ? <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: 24, color: G.muted, textAlign: "center" }}>{t.noBQ}</div>
            : bqs.map((bq, i) => {
              const alreadyDelegated = delegatedKeys[bq._key] || delegations.some(d => d.bq_id === bq._key);
              // Bara grupper med full poäng (4 = rätt på första försöket) kan få en
              // BQ delegerad — delpoäng betyder att de också famlade. Den frågande
              // gruppen filtreras bort; att delegera till sig själv ger inget.
              const correctGroups = results.filter(r => r.scores?.[bq.questionIndex] === 4 && r.groupName !== bq.groupName).map(r => r.groupName);
              return (
                <div key={i} style={{ background: G.surface, border: `2px solid ${G.burning}44`, borderRadius: 12, padding: "18px 22px", marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, gap: 12 }}>
                    <div style={{ display: "flex", gap: 10 }}>
                      <span>🔥</span>
                      <div><div style={{ fontWeight: 600, fontSize: 14 }}>{bq.groupName}</div><div style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, color: G.muted }}>IFAT Q{bq.iFATQuestion}</div></div>
                    </div>
                    {alreadyDelegated
                      ? <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, color: "#818cf8", background: "#6366f118", borderRadius: 999, padding: "4px 12px" }}>{t.delegated} {delegatedKeys[bq._key] || ""}</span>
                      : delegatingBQ === bq._key
                        ? correctGroups.length === 0
                          ? <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <span style={{ fontSize: 13, color: G.muted }}>{t.noFullScoreGroups}</span>
                            <button onClick={() => setDelegatingBQ(null)} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 8, color: G.muted, fontSize: 13, padding: "5px 10px", cursor: "pointer" }}>✕</button>
                          </div>
                          : <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <select value={delegateTarget} onChange={e => setDelegateTarget(e.target.value)} style={{ ...css.input, width: "auto", padding: "5px 10px", fontSize: 14 }}>
                            <option value="" style={{ background: G.surface }}>— {t.delegateTo} —</option>
                            {correctGroups.map(g => <option key={g} value={g} style={{ background: G.surface }}>{g}</option>)}
                          </select>
                          <button onClick={() => handleDelegate(bq)} style={{ background: "#6366f122", border: "1px solid #6366f144", borderRadius: 8, color: "#818cf8", fontSize: 13, padding: "5px 12px", cursor: "pointer" }}>→</button>
                          <button onClick={() => setDelegatingBQ(null)} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 8, color: G.muted, fontSize: 13, padding: "5px 10px", cursor: "pointer" }}>✕</button>
                        </div>
                        : <button onClick={() => { setDelegatingBQ(bq._key); setDelegateTarget(""); }} style={{ background: "#6366f122", border: "1px solid #6366f144", borderRadius: 8, color: "#818cf8", fontSize: 13, padding: "5px 14px", cursor: "pointer" }}>{t.delegate}</button>
                    }
                  </div>
                  <div style={{ fontSize: 14, color: G.text, lineHeight: 1.6, marginBottom: correctGroups.length > 0 ? 10 : 0 }}>{bq.text}</div>
                  {correctGroups.length > 0 && <div style={{ fontSize: 13, color: G.muted, marginBottom: 8 }}><span style={{ ...css.label, display: "inline", marginBottom: 0, fontSize: 10 }}>{t.correctGroups} </span>{correctGroups.map(g => <span key={g} style={{ fontFamily: "'DM Mono',monospace", background: G.correct + "18", color: G.correct, borderRadius: 6, padding: "1px 8px", marginLeft: 4 }}>{g}</span>)}</div>}
                  {/* Question preview toggle */}
                  <div>
                    <button onClick={() => setExpandedAppealQ(expandedAppealQ === bq._key ? null : bq._key)} style={{ background: "none", border: `1px solid ${expandedAppealQ === bq._key ? G.gold : G.border}`, borderRadius: 7, color: expandedAppealQ === bq._key ? G.gold : G.muted, fontSize: 12, padding: "3px 10px", cursor: "pointer" }}>
                      {expandedAppealQ === bq._key ? t.hideQuestion : t.showQuestion}
                    </button>
                  </div>
                  {expandedAppealQ === bq._key && (() => {
                    const qi = bq.questionIndex;
                    const qData = questions[qi];
                    const correctIdx = answerKey?.[qi];
                    if (!qData) return null;
                    return (
                      <div style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 12, padding: "12px 16px", marginTop: 8 }}>
                        {qData.text && <QuestionText text={qData.text} style={{ fontSize: 14, color: G.text, marginBottom: 8, fontWeight: 500 }} />}
                        {LABELS.map((lbl, oi) => (
                          <div key={lbl} style={{ fontSize: 13, color: oi === correctIdx ? G.correct : G.muted, marginBottom: 3 }}>
                            <strong style={{ fontFamily: "'DM Mono',monospace" }}>{lbl}{oi === correctIdx ? " ✓" : ""}</strong>
                            {qData.options?.[lbl] ? ` — ${qData.options[lbl]}` : ""}
                          </div>
                        ))}
                        {qData.rationale && <div style={{ fontSize: 13, color: G.goldDim, marginTop: 8, fontStyle: "italic" }}>💡 <RichText text={qData.rationale} /></div>}
                      </div>
                    );
                  })()}
                </div>
              );
            })
        )}

        {tab === "delegations" && (
          delegations.length === 0
            ? <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: 24, color: G.muted, textAlign: "center" }}>{t.noDelegations}</div>
            : delegations.map((d, i) => (
              <div key={i} style={{ background: G.surface, border: `2px solid #6366f144`, borderRadius: 12, padding: "18px 22px", marginBottom: 12 }}>
                <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                  <span>💬</span>
                  <div><div style={{ fontWeight: 600, fontSize: 14 }}>→ {d.toGroup}</div><div style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, color: G.muted }}>{t.delegationFrom}: {d.groupName} · Q{d.iFATQuestion}</div></div>
                </div>
                <div style={{ fontSize: 14, color: G.muted, marginBottom: 10, fontStyle: "italic" }}>{d.text}</div>
                {d.answer
                  ? <div style={{ background: G.correct + "18", border: `1px solid ${G.correct}33`, borderRadius: 12, padding: "12px 16px" }}>
                    <div style={{ ...css.label, color: G.correct, marginBottom: 6, fontSize: 10 }}>💬 {d.toGroup}'s answer:</div>
                    <div style={{ fontSize: 14, color: G.text, lineHeight: 1.6 }}>{d.answer}</div>
                  </div>
                  : <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, color: G.muted }}>{t.delegationAwaiting} {d.toGroup}…</div>
                }
              </div>
            ))
        )}

        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, color: G.muted, textAlign: "center", marginTop: 24 }}>
          {t.autoRefresh} · {t.lastUpdated} {new Date(lastRefresh).toLocaleTimeString()}
        </div>
        </>
        )}
      </div>
      <Footer G={G} />
      {showQuestions && session && (
        <QuestionPreview session={session} onClose={() => setShowQuestions(false)} lang={lang} G={G} css={css} />
      )}
      {confirmEnd && (
        <ConfirmDialog
          text={t.endTratConfirm}
          confirmLabel={t.endTratConfirmBtn}
          lang={lang}
          G={G}
          css={css}
          onCancel={() => setConfirmEnd(false)}
          onConfirm={applyToggleLock}
        />
      )}
    </div>
  );
}

export { DashboardScreen };
